import { type LandingSectionDefinition, type LandingSectionField } from "../../fields";
import {
  resetBonusesProps,
  resetCheckoutProps,
  resetFaqProps,
  resetGuaranteeProps,
  resetHeaderProps,
  resetHeroProps,
  resetLandingEffectsProps,
  resetLongTermProps,
  resetOfferProps,
  resetPainProps,
  resetRulesProps,
  resetSolutionProps,
  resetStackProps,
  resetStickyCtaProps,
  resetTestimonialProps,
  resetThankYouProps,
  resetTimelineProps,
} from "./content";
import { type resetSectionTypes } from "./types";

const GROUP = "Reset Metabolizmu 40+";
const GROUP_CHECKOUT = "Reset Metabolizmu 40+ - checkout";

const COHORT_HELP = "{{startGrupy}} = 19.10, {{startGrupyDzien}} = pon. 19.10 (grupa Resetu).";
const RICH_HELP = "<strong> pogrubia, <em> wyróżnia kolorem.";

const text = (key: string, label: string, help?: string): LandingSectionField => ({
  key,
  label,
  type: "text",
  ...(help ? { help } : {}),
});
const textarea = (key: string, label: string, help?: string): LandingSectionField => ({
  key,
  label,
  type: "textarea",
  ...(help ? { help } : {}),
});
const rich = (key: string, label: string, help = RICH_HELP): LandingSectionField => ({
  key,
  label,
  type: "richText",
  help,
});
const bool = (key: string, label: string, help?: string): LandingSectionField => ({
  key,
  label,
  type: "boolean",
  ...(help ? { help } : {}),
});
const strings = (key: string, label: string, help?: string): LandingSectionField => ({
  key,
  label,
  type: "stringList",
  ...(help ? { help } : {}),
});
const list = (
  key: string,
  label: string,
  itemLabel: string,
  fields: LandingSectionField[],
  help?: string,
): LandingSectionField => ({ key, label, type: "list", itemLabel, fields, ...(help ? { help } : {}) });

const eyebrow = text("eyebrow", "Nadtytuł");
const heading = rich("heading", "Nagłówek", "<strong> pogrubia fragment.");

export const resetSectionDefinitions: Record<(typeof resetSectionTypes)[number], LandingSectionDefinition> = {
  resetLandingEffects: {
    label: "Śledzenie landingu Resetu (niewidoczne)",
    group: GROUP,
    fields: [
      text("stepSlug", "step_slug w zdarzeniu landing_viewed"),
      bool("facebookPixel", "Piksel Facebooka Programu Hollywood"),
    ],
    defaultProps: resetLandingEffectsProps,
  },
  resetHeader: {
    label: "Nagłówek strony (logo i przycisk)",
    group: GROUP,
    fields: [
      text("ctaLabel", "Przycisk", "Przewija do oferty."),
      bool("hidden", "Niewidoczny (zajmuje miejsce)", "Tak jest na stronie Resetu z kodu."),
    ],
    defaultProps: resetHeaderProps,
  },
  resetHero: {
    label: "Hero Resetu",
    group: GROUP,
    needs: ["cohort"],
    fields: [
      text("badge", "Plakietka nad tytułem", COHORT_HELP),
      bool("showSeats", "Dopisz wolne miejsca w plakietce"),
      rich("title", "Tytuł", "<strong> pogrubia, <mark> podkreśla falą."),
      textarea("subtitle", "Podtytuł"),
      strings("bullets", "Punkty z ✅"),
      text("ctaLabel", "Przycisk", "Przewija do oferty."),
      text("ctaNote", "Druga linia przycisku", "{{cena}} = cena z funnelu."),
      list("trust", "Pasek zaufania", "Element", [
        text("icon", "Emoji"),
        text("boldText", "Pogrubiony tekst"),
        text("rest", "Reszta"),
      ]),
    ],
    defaultProps: resetHeroProps,
  },
  resetPain: {
    label: "Czy to brzmi znajomo? (problemy)",
    group: GROUP,
    fields: [
      heading,
      textarea("intro", "Wstęp"),
      list("cards", "Karty", "Karta", [text("emoji", "Emoji"), text("title", "Tytuł"), textarea("body", "Treść")]),
      rich("calloutTitle", "Ciemna ramka: tytuł", "<strong> pogrubia na różowo."),
      strings("calloutParagraphs", "Ciemna ramka: akapity"),
    ],
    defaultProps: resetPainProps,
  },
  resetRules: {
    label: "3 zasady gry po 40-tce",
    group: GROUP,
    fields: [
      eyebrow,
      heading,
      list("items", "Zasady", "Zasada", [
        text("num", "Numer"),
        text("emoji", "Emoji"),
        text("title", "Tytuł"),
        text("sub", "Podpis"),
        textarea("body", "Treść"),
      ]),
      text("calloutTitle", "Czerwona ramka: tytuł"),
      rich("calloutText", "Czerwona ramka: tekst"),
    ],
    defaultProps: resetRulesProps,
  },
  resetSolution: {
    label: "Rozwiązanie (ciemna sekcja)",
    group: GROUP,
    fields: [
      eyebrow,
      rich("heading", "Nagłówek", "<strong> pogrubia na różowo."),
      textarea("intro", "Wstęp"),
      list("cards", "Karty", "Karta", [text("num", "Numer"), text("title", "Tytuł"), textarea("body", "Treść")]),
      text("closingText", "Zakończenie"),
      text("closingBold", "Zakończenie (pogrubione)"),
    ],
    defaultProps: resetSolutionProps,
  },
  resetStack: {
    label: "Co dostajesz (lista z wartościami)",
    group: GROUP,
    fields: [
      eyebrow,
      heading,
      list("items", "Elementy", "Element", [
        text("icon", "Emoji"),
        text("title", "Tytuł"),
        textarea("body", "Opis"),
        text("priceValue", "Wartość", "„w cenie” ukrywa napis „Wartość:”."),
        text("priceSub", "Dopisek pod wartością"),
      ]),
      text("totalLabel", "Łączna wartość: etykieta"),
      text("totalValue", "Łączna wartość (przekreślona)"),
      text("investmentLabel", "Inwestycja: etykieta"),
      text("price", "Cena", "{{cena}} = cena z funnelu."),
      text("priceAlt", "Druga linia ceny", "{{raty}} = raty z funnelu."),
    ],
    defaultProps: resetStackProps,
  },
  resetBonuses: {
    label: "Bonusy dla grupy",
    group: GROUP,
    needs: ["cohort"],
    fields: [
      rich("heading", "Nagłówek", `<strong> pogrubia. ${COHORT_HELP}`),
      textarea("intro", "Wstęp"),
      list("items", "Bonusy", "Bonus", [
        text("tag", "Etykieta"),
        text("emoji", "Emoji"),
        text("title", "Tytuł"),
        textarea("body", "Opis"),
        text("priceValue", "Wartość"),
      ]),
    ],
    defaultProps: resetBonusesProps,
  },
  resetGuarantee: {
    label: "Podwójna gwarancja",
    group: GROUP,
    fields: [
      text("badgeValue", "Koło: duży tekst"),
      text("badgeLabel", "Koło: podpis"),
      heading,
      text("intro", "Wstęp"),
      strings("checklist", "Warunki (lista z ✓)"),
      text("dividerLabel", "Tekst na linii"),
      list("options", "Opcje", "Opcja", [text("icon", "Emoji"), rich("text", "Tekst")]),
      text("closing", "Zdanie końcowe"),
      textarea("footnote", "Przypis"),
    ],
    defaultProps: resetGuaranteeProps,
  },
  resetTestimonial: {
    label: "Historia klientki z filmem",
    group: GROUP,
    fields: [
      eyebrow,
      heading,
      text("videoId", "ID filmu Wistia"),
      text("badgeTop", "Plakietka: wynik"),
      text("badgeSub", "Plakietka: podpis"),
      text("avatar", "Emoji awatara"),
      text("who", "Kto"),
      text("meta", "Opis"),
      textarea("quote", "Cytat"),
    ],
    defaultProps: resetTestimonialProps,
  },
  resetLongTerm: {
    label: "Co dalej po Resecie (metamorfozy)",
    group: GROUP,
    fields: [
      eyebrow,
      heading,
      textarea("intro", "Wstęp"),
      strings("transformationIds", "ID metamorfoz", "Puste = 6 metamorfoz z filmem o największej utracie wagi."),
      text("closing", "Zdanie pod siatką"),
    ],
    defaultProps: resetLongTermProps,
  },
  resetTimeline: {
    label: "Oś czasu 6 tygodni",
    group: GROUP,
    fields: [
      eyebrow,
      heading,
      list("weeks", "Tygodnie", "Tydzień", [
        text("meta", "Nadtytuł"),
        text("title", "Tytuł"),
        textarea("body", "Opis"),
      ]),
    ],
    defaultProps: resetTimelineProps,
  },
  resetOffer: {
    label: "Oferta z przyciskiem do checkoutu",
    group: GROUP,
    needs: ["cohort"],
    fields: [
      { key: "href", label: "Link przycisku", type: "url", help: "Puste = następny krok funnelu (checkout)." },
      text("deadlineLabel", "Termin zapisów", COHORT_HELP),
      text("countdownLabel", "Odliczanie: etykieta"),
      heading,
      textarea("intro", "Wstęp"),
      strings("benefits", "Korzyści (→)", "<strong> pogrubia."),
      text("cardEyebrow", "Karta: nadtytuł"),
      text("cardTitle", "Karta: tytuł"),
      text("valueLabel", "Wartość: etykieta"),
      text("valueStrike", "Wartość (przekreślona)"),
      text("investmentLabel", "Inwestycja: etykieta"),
      text("price", "Cena", "{{cena}} = cena z funnelu."),
      rich("priceAlt", "Druga linia ceny", "{{raty}} = raty z funnelu, <strong> pogrubia."),
      rich("startText", "Start grupy", COHORT_HELP),
      bool("showSeats", "Dopisz wolne miejsca"),
      text("ctaLabel", "Przycisk", "{{cena}} = cena z funnelu."),
      text("ctaNote", "Druga linia przycisku"),
      strings("badges", "Plakietki pod przyciskiem", COHORT_HELP),
      rich("closing", "Zdanie końcowe", `<strong> pogrubia. ${COHORT_HELP}`),
    ],
    defaultProps: resetOfferProps,
  },
  resetFaq: {
    label: "FAQ Resetu",
    group: GROUP,
    needs: ["cohort"],
    fields: [
      eyebrow,
      heading,
      list(
        "items",
        "Pytania",
        "Pytanie",
        [text("q", "Pytanie"), textarea("a", "Odpowiedź")],
        `${COHORT_HELP} {{ratyOpisowo}} = 2 raty po 217 zł.`,
      ),
    ],
    defaultProps: resetFaqProps,
  },
  resetStickyCta: {
    label: "Przyklejony pasek z ceną (telefon)",
    group: GROUP,
    fields: [
      text("productLabel", "Nad ceną"),
      text("note", "Pod ceną"),
      text("ctaLabel", "Przycisk", "Przewija do oferty."),
    ],
    defaultProps: resetStickyCtaProps,
  },
  resetCheckout: {
    label: "Checkout Resetu (formularz płatności)",
    group: GROUP_CHECKOUT,
    needs: ["cohort"],
    fields: [
      text("seoTitle", "Tytuł karty przeglądarki"),
      rich("heroTitle", "Tytuł", "<strong> pogrubia."),
      rich("heroSubtitle", "Podtytuł", COHORT_HELP),
      bool("showSeats", "Dopisz wolne miejsca w podtytule"),
      text("productIcon", "Produkt: emoji"),
      text("productName", "Produkt: nazwa"),
      text("productTagline", "Produkt: opis"),
      strings("productItems", "Produkt: co dostajesz"),
      rich("guaranteeText", "Ramka gwarancji", "<strong> pogrubia."),
      textarea("guaranteeDetails", "Ramka gwarancji: „Jak to działa?”"),
      text("phoneHelpText", "Podpis pola telefonu"),
      text("countdownLabel", "Odliczanie nad przyciskiem", COHORT_HELP),
      text("payButtonLabel", "Przycisk płatności", "{{kwota}} = kwota płacona dziś."),
      text("payButtonNote", "Przycisk płatności: druga linia"),
      list(
        "afterPaymentSteps",
        "Co się stanie po płatności",
        "Krok",
        [text("when", "Kiedy"), textarea("what", "Co")],
        COHORT_HELP,
      ),
      list("faq", "FAQ pod formularzem", "Pytanie", [
        text("q", "Pytanie"),
        textarea("a", "Odpowiedź"),
        textarea("aInstallments", "Odpowiedź przy ratach", "Opcjonalna. {{rata}} = kwota kolejnej raty."),
      ]),
      text("stickyLabel", "Przyklejony pasek: podpis"),
    ],
    defaultProps: resetCheckoutProps,
  },
  resetThankYou: {
    label: "Podziękowanie Resetu (podsumowanie zamówienia)",
    group: GROUP_CHECKOUT,
    fields: [
      text("eyebrow", "Nadtytuł"),
      text("title", "Tytuł"),
      textarea("description", "Opis"),
      text("renewalTitle", "Tytuł przy odnowieniu"),
      textarea("renewalDescription", "Opis przy odnowieniu"),
      text("orderNumberLabel", "Etykieta: numer zamówienia"),
      text("billingLabel", "Etykieta: dane kupującego"),
      text("paymentMethodLabel", "Etykieta: metoda płatności"),
      text("paidAmountLabel", "Etykieta: zapłacona kwota"),
      text("firstPaymentLabel", "Etykieta: pierwsza płatność", "{{data}} = data pierwszej płatności."),
      bool("confetti", "Konfetti"),
    ],
    defaultProps: resetThankYouProps,
  },
};
